import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Flame } from "lucide-react";
import { Fragment, lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, Empty, ErrorNote, Spinner } from "@/components/ui";
import { useToast } from "@/components/Toast";
import { AutoAppliedPanel } from "@/components/AutoApplied";
import { OutcomePill, QuickLog } from "@/components/CallLog";
import { PhoneLink } from "@/components/PhoneLink";
import { TextButton } from "@/components/TextButton";
import { lastCallPill } from "@/lib/callLog";
import { api, type CallTodayRow, type OverviewItem, type TaskRow } from "@/lib/api";
import { formatDateTime, formatDue, formatMoneyCents } from "@/lib/format";
import { leadHref } from "@/lib/leadList";
import { STAGE_HELP, STAGE_LABEL, stageTone } from "@/lib/stages";
import type { LeadStage } from "@/lib/api";

const QUEUE_LIMIT = 12;
// Matches api/routers/crm.py's SUGGESTION_LIMIT: a queue of decisions is only a queue while it is short
// enough to work through.
const SUGGESTION_LIMIT = 15;
const TASK_LIMIT = 25;

function QueueRow({
  item,
  onDone,
  list,
  index,
}: {
  item: OverviewItem;
  onDone: () => void;
  /** The list id the lead page steps through, when this row belongs to a workable queue (D-32). */
  list?: string;
  index?: number;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const touch = useMutation({
    mutationFn: () => api.touchLead(item.id),
    onSuccess: () => {
      toast.push("Marked touched");
      void qc.invalidateQueries();
      onDone();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });
  const snooze = useMutation({
    mutationFn: (days: 1 | 3 | 7) => api.snoozeLead(item.id, days),
    onSuccess: (_d, days) => {
      toast.push(`Snoozed ${days} day${days === 1 ? "" : "s"}`);
      void qc.invalidateQueries();
      onDone();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  const isLead = item.entity_type === "lead";
  return (
    <li className="flex items-center gap-2 border-b px-3 py-1.5 last:border-b-0" data-testid="queue-row">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {isLead ? (
            <Link
              to={list ? leadHref(item.id, list, index ?? 0) : `/leads/${item.id}`}
              className="truncate text-sm font-medium hover:underline"
            >
              {item.title}
            </Link>
          ) : item.entity_type === "cv_deal" ? (
            <Link to={`/curative/deals/${item.id}`} className="truncate text-sm font-medium hover:underline">
              {item.title}
            </Link>
          ) : (
            <span className="truncate text-sm font-medium">{item.title}</span>
          )}
          {item.hot && (
            <Flame className="size-3.5 shrink-0 text-orange-600" fill="currentColor" aria-label="hot" data-testid="queue-hot" />
          )}
          {item.stage && item.entity_type === "cv_deal" && <Badge tone="muted">{item.stage}</Badge>}
          {item.stage && item.entity_type !== "cv_deal" && (
            <Badge
              tone={stageTone(item.stage as LeadStage)}
              title={STAGE_HELP[item.stage as LeadStage]}
            >
              {STAGE_LABEL[item.stage as LeadStage] ?? item.stage}
            </Badge>
          )}
        </div>
        {item.subtitle && <p className="truncate text-xs text-fg-muted">{item.subtitle}</p>}
      </div>
      {item.due_at && (
        <span
          className={
            "shrink-0 text-xs tabular-nums " +
            (item.severity === "danger" ? "text-danger" : "text-fg-muted")
          }
        >
          {formatDue(item.due_at)}
        </span>
      )}
      {isLead && (
        <div className="flex shrink-0 items-center gap-1">
          {item.sc_contact_url && (
            <a
              href={item.sc_contact_url}
              target="_blank"
              rel="noreferrer"
              title="Open in SmarterContact"
              className="rounded px-1.5 py-0.5 text-xs text-fg-muted hover:bg-surface-2 hover:text-fg"
            >
              SC
            </a>
          )}
          <Button size="sm" variant="ghost" onClick={() => touch.mutate()} title="Mark touched">
            Touch
          </Button>
          {([1, 3, 7] as const).map((d) => (
            <Button
              key={d}
              size="sm"
              variant="ghost"
              onClick={() => snooze.mutate(d)}
              title={`Snooze ${d} day${d === 1 ? "" : "s"}`}
            >
              {d}d
            </Button>
          ))}
        </div>
      )}
    </li>
  );
}

/**
 * Silently dropping rows from a work queue hides work, so say what is not shown — and link
 * straight to the filtered view that holds the rest, rather than leaving the reader to
 * reconstruct the filter by hand.
 */
export function Truncated({ shown, total, href }: { shown: number; total: number; href: string }) {
  if (total <= shown) return null;
  return (
    <li className="px-3 py-1.5 text-[11px]" data-testid="truncated-note">
      <Link to={href} className="text-brand underline">
        + {total - shown} more
      </Link>
      <span className="text-fg-muted"> — open the full list.</span>
    </li>
  );
}

function TaskLine({ task }: { task: TaskRow }) {
  const toast = useToast();
  const qc = useQueryClient();
  const complete = useMutation({
    mutationFn: () => api.updateTask(task.id, { done: true }),
    onSuccess: () => {
      toast.push("Task completed");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });
  return (
    <li className="flex items-center gap-2 border-b px-3 py-1.5 last:border-b-0" data-testid="task-row">
      <input
        type="checkbox"
        aria-label={`Complete ${task.title}`}
        onChange={() => complete.mutate()}
        className="size-3.5"
      />
      <div className="min-w-0 flex-1">
        <span className="block truncate text-sm">{task.title}</span>
        {(task.lead_name ?? task.notes) && (
          <span className="block truncate text-xs text-fg-muted">
            {task.lead_name ?? task.notes}
          </span>
        )}
      </div>
      <Badge tone={task.priority === "p1" ? "danger" : task.priority === "p2" ? "warn" : "muted"}>
        {task.priority}
      </Badge>
      {task.due_at && (
        <span className="shrink-0 text-xs tabular-nums text-fg-muted">{formatDue(task.due_at)}</span>
      )}
    </li>
  );
}

/** §7.2 Overview: today's queue, each row with one-click actions. */
export function Overview() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["overview"],
    queryFn: () => api.overview(),
  });

  if (isLoading) return <Spinner label="Loading today's queue…" />;
  if (error) return <div className="p-4"><ErrorNote error={error} /></div>;
  if (!data) return null;

  const onDone = () => {
    void refetch();
    void qc.invalidateQueries({ queryKey: ["nav-counts"] });
  };

  const sections: {
    key: string;
    title: string;
    items: OverviewItem[];
    empty: string;
    href: string;
    /** Rows to show before "+N more"; defaults to QUEUE_LIMIT. */
    limit?: number;
    /** The true total when the server capped what it sent, so "+N more" is not silently wrong. */
    total?: number;
  }[] = [
    // v2.2 P3.1: HOT opens the page — the leads and deals someone flagged for a priority call.
    // (v2.6: under CALL TODAY, which is rendered on its own above the grid.)
    {
      key: "hot",
      title: `HOT (${data.counts.hot ?? 0})`,
      items: data.hot ?? [],
      empty: "Nothing flagged hot. Press H on a lead, or the flame on a card.",
      href: "/leads?hot=1",
    },
    // v2.6 P2 (D-98): what R2 (or a person) parked for Friday 09:00 ET — one place to work them.
    {
      key: "friday-sweep",
      title: `Friday sweep (${data.counts.friday_sweep ?? 0})`,
      items: data.friday_sweep ?? [],
      empty: "Nothing parked for Friday's sweep.",
      href: "/leads",
    },
    {
      key: "overdue",
      title: `Overdue next actions (${data.counts.overdue_actions ?? 0})`,
      items: data.overdue_actions,
      empty: "Nothing overdue.",
      href: "/leads?overdue=true&sort=next_action_due&order=asc",
    },
    {
      key: "due-soon",
      title: `Next actions due within 3 days (${data.counts.actions_due_soon ?? 0})`,
      items: data.actions_due_soon,
      empty: "Nothing due in the next three days.",
      href: "/leads?sort=next_action_due&order=asc",
    },
    {
      key: "kill",
      title: `Kill-check banners (${data.counts.kill_checks ?? 0})`,
      items: data.kill_checks,
      empty: "No deal is tripping a kill check.",
      href: "/deals?kill=true",
    },
    {
      key: "engagements",
      title: `Engagements due within 3 days (${data.counts.engagements_due ?? 0})`,
      items: data.engagements_due,
      empty: "No vendor work is due.",
      href: "/engagements?due=soon",
    },
    {
      key: "stale",
      title: `Stale leads (${data.counts.stale_leads ?? 0})`,
      items: data.stale_leads,
      empty: "Nothing has gone stale.",
      href: "/leads?stale=true&sort=last_touched_at&order=asc",
    },
    {
      key: "suggestions",
      title: `Pending agent suggestions (${data.counts.pending_suggestions ?? 0})`,
      items: data.pending_suggestions,
      empty: "No suggestions waiting.",
      href: "/leads?has_suggestion=true&sort=score&order=desc",
      // Highest score first, and the server caps the fetch at the same number — so "+N more" appears
      // when there are more than fifteen, and links to the same ordering rather than a different one.
      limit: SUGGESTION_LIMIT,
      total: data.counts.pending_suggestions ?? data.pending_suggestions.length,
    },
  ];

  return (
    <div className="p-4">
      <header className="mb-3">
        <h1 className="text-lg font-semibold tracking-tight">Today</h1>
        <p className="text-xs text-fg-muted">
          Everything that needs a decision, in the order it needs one.
        </p>
      </header>
      <CallToday rows={data.call_today ?? []} />
      {/* v2.10 P2.3 / P2.4 / P3.4: callbacks, awaiting your confirm, texts to send. */}
      <Suspense fallback={null}>
        <VoiceTodayPanels />
      </Suspense>
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {sections.map((s) => (
          <Card
            key={s.key}
            title={s.title}
            className="overflow-hidden"
            data-testid={`section-${s.key}`}
            action={s.key === "suggestions" ? <AcceptAll /> : undefined}
          >
            {s.items.length === 0 ? (
              <Empty title={s.empty} />
            ) : (
              <ul className="-mx-3 -my-3">
                {s.items.slice(0, s.limit ?? QUEUE_LIMIT).map((item, index) => (
                  <QueueRow
                    key={`${item.kind}-${item.id}`}
                    item={item}
                    onDone={onDone}
                    // Only the review queue is a list you WORK: the others are diagnostics, and stepping
                    // through them with ← / → would be stepping through a symptom (D-32).
                    list={s.key === "suggestions" ? "today" : undefined}
                    index={index}
                  />
                ))}
                <Truncated
                  shown={s.limit ?? QUEUE_LIMIT}
                  total={s.total ?? s.items.length}
                  href={s.href}
                />
              </ul>
            )}
          </Card>
        ))}
        <Card title={`Open tasks (${data.counts.open_tasks ?? 0})`} className="overflow-hidden" data-testid="section-tasks">
          {data.open_tasks.length === 0 ? (
            <Empty title="No open tasks." />
          ) : (
            <ul className="-mx-3 -my-3" data-testid="open-tasks">
              {data.open_tasks.slice(0, TASK_LIMIT).map((t) => (
                <TaskLine key={t.id} task={t} />
              ))}
              <Truncated shown={TASK_LIMIT} total={data.open_tasks.length} href="/tasks" />
            </ul>
          )}
        </Card>
        {/* Tier 1 of the trust ladder: what the model changed on its own, and one click to undo it
            (D-33). On Today rather than buried on each lead, because the operator's question is "what
            happened while I was not looking" and that question is about all of them at once. */}
        <AutoAppliedPanel />
      </div>
    </div>
  );
}

// v2.10: the dial session and the Voice lists load on demand (the entry bundle's 200 kB budget).
const DialSession = lazy(() => import("@/components/DialSession").then((m) => ({ default: m.DialSession })));
const VoiceTodayPanels = lazy(() =>
  import("@/components/VoiceToday").then((m) => ({ default: m.VoiceTodayPanels })),
);

/**
 * v2.6 P4 (D-102): CALL TODAY, above HOT — the sellers who said they are interested or asked a price.
 * A stated number first, then the biggest parcel, then whoever is due soonest. The phone is `tel:` plus
 * Google Voice's dialer: nothing here places a call (Rule 0), Sam presses the button.
 */
/** v2.7 P2.5: how many CALL TODAY rows show before "Show all" (prod has 18, so all of them today). */
const CALL_TODAY_FIRST = 25;

function CallToday({ rows: everyRow }: { rows: CallTodayRow[] }) {
  const qc = useQueryClient();
  // v2.7 P2.5: the board's "n of m" convention — past CALL_TODAY_FIRST rows the header says how many of
  // how many are on screen, and one click shows the rest; it never shows a bare count over a partial list.
  const [everyone, setEveryone] = useState(false);
  // v2.9 P3.6: a seller whose next attempt is still in the future (a no-answer two days out, a callback
  // at 3 pm) is not someone to call now — hidden until "Show all".
  const now = Date.now();
  const due = everyRow.filter(
    (r) => !r.last_call?.next_attempt_at || new Date(r.last_call.next_attempt_at).getTime() <= now,
  );
  const all = everyone ? everyRow : due;
  const rows = everyone ? all : all.slice(0, CALL_TODAY_FIRST);
  const hidden = everyRow.length - rows.length;
  const partial = hidden > 0;
  // v2.9 P1.4: `L` opens the quick-log chips on the row under the mouse (else the first one).
  const [active, setActive] = useState(0);
  const focus = useRef<Record<string, { current: (() => void) | null }>>({});
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const t = event.target;
      if (t instanceof HTMLElement && (["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable)) return;
      if (event.key !== "l" && event.key !== "L") return;
      const row = rows[Math.min(active, rows.length - 1)];
      if (!row) return;
      event.preventDefault();
      focus.current[row.lead_id]?.current?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rows, active]);
  // v2.10 P2.5: the dial session walks the rows that are due, one card at a time.
  const [session, setSession] = useState(false);
  return (
    <Card
      title={`CALL TODAY (${partial ? `${rows.length} of ${everyRow.length}` : everyRow.length})`}
      className="mb-3 overflow-hidden"
      data-testid="section-call-today"
      action={
        <span className="flex items-center gap-2">
          {due.length > 0 && (
            <Button size="sm" variant="primary" onClick={() => setSession(true)} data-testid="dial-session-start">
              Start dial session
            </Button>
          )}
          {partial && (
            <button type="button" className="text-xs text-accent underline" onClick={() => setEveryone(true)} data-testid="call-today-all">
              Show all {everyRow.length}
            </button>
          )}
        </span>
      }
    >
      {session && (
        <Suspense fallback={null}>
          <DialSession rows={due} onClose={() => setSession(false)} />
        </Suspense>
      )}
      {rows.length === 0 ? (
        <Empty title={everyRow.length ? "Everyone here has a next attempt later." : "Nobody interested or asking a price right now."} />
      ) : (
        <div className="-mx-3 -my-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-fg-muted">
              <tr>
                <th className="px-3 py-1.5 font-medium">Name</th>
                <th className="px-3 py-1.5 font-medium">Phone</th>
                <th className="px-3 py-1.5 font-medium">Last call</th>
                <th className="px-3 py-1.5 font-medium">Stated number</th>
                <th className="px-3 py-1.5 font-medium">Acres</th>
                <th className="px-3 py-1.5 font-medium">Last inbound</th>
                <th className="px-3 py-1.5 font-medium">Next action</th>
                <th className="px-3 py-1.5 font-medium" title="One click logs the call (L on the row)">Log</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <Fragment key={r.lead_id}>
                  <tr className="border-t" data-testid="call-today-row" data-lead-id={r.lead_id} onMouseEnter={() => setActive(i)}>
                    <td className="px-3 py-1.5">
                      <Link to={`/leads/${r.lead_id}`} className="font-medium hover:underline">
                        {r.name}
                      </Link>
                      <span className="ml-1 text-fg-muted">{r.reply_class.replace("_", " ")}</span>
                    </td>
                    <td className="px-3 py-1.5">
                      <span className="inline-flex items-center gap-1">
                        <PhoneLink e164={r.phone_e164} compact leadId={r.lead_id} surface="call-today" />
                        {r.phone_e164 && (
                          <TextButton
                            owner={{ kind: "lead", id: r.lead_id }}
                            phones={[r.phone_e164]}
                            phone={r.phone_e164}
                            compact
                            testId="call-today-text"
                          />
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-1.5" data-testid="call-today-last">
                      {lastCallPill(r.last_call) ? (
                        <OutcomePill outcome={r.last_call?.outcome}>{lastCallPill(r.last_call)}</OutcomePill>
                      ) : (
                        <span className="text-fg-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums" data-testid="call-today-price">
                      {r.asking_price_cents != null ? formatMoneyCents(r.asking_price_cents) : "—"}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums">{r.acres ?? "—"}</td>
                    <td className="px-3 py-1.5">{r.last_inbound_at ? formatDateTime(r.last_inbound_at) : "—"}</td>
                    <td className="px-3 py-1.5">
                      {r.next_action ?? "—"}
                      {r.next_action_due && <span className="ml-1 text-fg-muted">{formatDue(r.next_action_due)}</span>}
                    </td>
                    <td className="px-3 py-1.5" data-testid="call-today-quick">
                      <QuickLog
                        compact
                        owner={{ kind: "lead", id: r.lead_id }}
                        phones={r.phone_e164 ? [r.phone_e164] : []}
                        focusRef={(focus.current[r.lead_id] ??= { current: null })}
                        onLogged={() => void qc.invalidateQueries({ queryKey: ["overview"] })}
                      />
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/**
 * "Accept all >= 0.90" — the bulk case of the Tier-2 queue (D-33).
 *
 * Each lead still goes through the ordinary accept path on the server, so every §6.1 check, every ledger
 * event and every feedback row is identical to what a click produces. The only thing being automated is
 * the clicking. The threshold is above the auto-apply line on purpose: these are suggestions the ladder
 * decided a person should see, and this says "I have seen them".
 */
function AcceptAll() {
  const toast = useToast();
  const qc = useQueryClient();
  const accept = useMutation({
    mutationFn: () => api.acceptAllSuggestions(),
    onSuccess: (out) => {
      toast.push(
        out.count === 0
          ? "Nothing pending at 0.90 or above"
          : `${out.count} suggestion${out.count === 1 ? "" : "s"} accepted`,
      );
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });
  return (
    <Button
      size="sm"
      variant="ghost"
      disabled={accept.isPending}
      onClick={() => accept.mutate()}
      data-testid="accept-all"
      title="Accept every pending suggestion at confidence 0.90 or above, one by one, as you"
    >
      {accept.isPending ? "Accepting…" : "Accept all ≥ 0.90"}
    </Button>
  );
}
